/**
 * Upcoming membership charges forecast (DNKADM-0377, DNKAPI-0533).
 *
 * `GET /v2/admin/memberships/upcoming-charges` — OWNER/SUPER_ADMIN/ADMIN.
 * STAFF is 403. Tenant from the JWT. Bearer-only; no cookies; no CSRF.
 * `locationId` is the app-bar `v2_location.id` (never a GHL location id).
 *
 * Paging is 0-indexed. Default `size` 20 (max 100). Omit `from`/`to` for the
 * server default window (tenant-local today through today+31 inclusive).
 *
 * Envelope is the existing page shape: `{ content, page, size, totalElements,
 * totalPages }` plus, since DNKAPI-0536, a `summary` object saying what the
 * page's total covers. Defensive parse (mirrors `listCardOnFileReservations` /
 * `paymentsTransactions.ts`): missing fields → null, drop rows without
 * `contactId` or `nextBillingOn`, never throw on a soft-bad row.
 * `amountIsEstimate` is coerced true. `subscriptionStatus` is parsed and must
 * not be rendered by the caller.
 *
 * DNKADM-0383: the page total is the SERVER's (`summary.pricedAmount`), never a
 * client-side sum of `amount` — a row on a negotiated rate carries no amount and
 * `rateBasis: 'negotiated'`, and a sum that quietly skipped it would be the
 * wrong forecast. A missing/malformed `summary` parses to `null` and the caller
 * shows no total rather than computing one.
 */
import { request } from './client';

const PATH = '/v2/admin/memberships/upcoming-charges';

/** Known subscription statuses on the wire. Unknown values pass through as text. */
export type UpcomingMembershipSubscriptionStatus =
  | 'active'
  | 'paused'
  | 'past_due'
  | (string & {});

/** One parsed upcoming-membership-charge row. */
export interface UpcomingMembershipCharge {
  contactId: string;
  contactName: string | null;
  planId: number | null;
  planName: string | null;
  /** `YYYY-MM-DD` — always present on a kept row. */
  nextBillingOn: string;
  /** Major units, or null. Never treat null as 0. */
  amount: number | null;
  /** ISO-4217; null iff `amount` is null. */
  currency: string | null;
  /** Always true — the column header already says est. */
  amountIsEstimate: true;
  pauseEffectiveOn: string | null;
  maskedCard: string | null;
  /** Parsed for the type; do not render. */
  subscriptionStatus: UpcomingMembershipSubscriptionStatus | null;
  locationId: number | null;
  /**
   * DNKAPI-0536: what the subscription bills at. `catalog` — the list price (and
   * `amount` is that price plus tax); `negotiated` — a per-member rate the API
   * cannot read within its read budget, so `amount` is null and the figure lives
   * with the member; `null` — the server cannot tell (also `amount: null`).
   */
  rateBasis: UpcomingMembershipRateBasis | null;
}

/** DNKAPI-0536 `rateBasis` values. Unknown wire values parse to `null`. */
export type UpcomingMembershipRateBasis = 'catalog' | 'negotiated';

/**
 * DNKAPI-0536 page summary: what this page's total covers. `pricedAmount` is the
 * sum of `amount` over the PRICED rows on the page — the only total this screen
 * may show — and is `null` when no row on the page is priced.
 */
export interface UpcomingMembershipChargesSummary {
  /** Major units, or null when nothing on the page is priced. Never treat null as 0. */
  pricedAmount: number | null;
  /** ISO-4217; null iff `pricedAmount` is null. */
  currency: string | null;
  /** Rows on this page that contribute to `pricedAmount`. */
  pricedRows: number;
  /** Rows on this page that do not (negotiated rates, unpriceable plans). */
  unpricedRows: number;
}

/** Parsed page from `GET /v2/admin/memberships/upcoming-charges`. */
export interface UpcomingMembershipChargesPage {
  content: UpcomingMembershipCharge[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  /** The server's page summary, or `null` when the response carried none. */
  summary: UpcomingMembershipChargesSummary | null;
}

export interface ListUpcomingMembershipChargesParams {
  /** App-bar v2 location id; omitted means no location filter. */
  locationId?: string;
  /** 0-indexed wire page. */
  page?: number;
  size?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function extractRows(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body['content'])) return body['content'];
  return [];
}

function parseRow(raw: unknown): UpcomingMembershipCharge | null {
  const o = isObject(raw) ? raw : {};
  const contactId = asText(o['contactId']);
  const nextBillingOn = asText(o['nextBillingOn']);
  if (contactId === null || nextBillingOn === null) return null;

  const rawAmount = asNumber(o['amount']);
  const rawCurrency = asText(o['currency']);
  const amount = rawAmount !== null && rawCurrency !== null ? rawAmount : null;
  const currency = amount !== null ? rawCurrency : null;

  return {
    contactId,
    contactName: asText(o['contactName']),
    planId: asNumber(o['planId']),
    planName: asText(o['planName']),
    nextBillingOn,
    amount,
    currency,
    amountIsEstimate: true,
    pauseEffectiveOn: asText(o['pauseEffectiveOn']),
    maskedCard: asText(o['maskedCard']),
    subscriptionStatus: asText(o['subscriptionStatus']),
    locationId: asNumber(o['locationId']),
    rateBasis: parseRateBasis(o['rateBasis']),
  };
}

function parseRateBasis(value: unknown): UpcomingMembershipRateBasis | null {
  return value === 'catalog' || value === 'negotiated' ? value : null;
}

/**
 * The page summary, or `null` when absent or not an object. `pricedAmount`
 * survives only with a currency (the same rule as a row's `amount`); the row
 * counts default to 0 when missing so a malformed count never reads as "priced".
 */
function parseSummary(raw: unknown): UpcomingMembershipChargesSummary | null {
  if (!isObject(raw)) return null;
  const rawAmount = asNumber(raw['pricedAmount']);
  const rawCurrency = asText(raw['currency']);
  const pricedAmount = rawAmount !== null && rawCurrency !== null ? rawAmount : null;
  return {
    pricedAmount,
    currency: pricedAmount !== null ? rawCurrency : null,
    pricedRows: Math.max(0, Math.trunc(asNumber(raw['pricedRows']) ?? 0)),
    unpricedRows: Math.max(0, Math.trunc(asNumber(raw['unpricedRows']) ?? 0)),
  };
}

/**
 * `GET /v2/admin/memberships/upcoming-charges` → a read-only, paged forecast of
 * known membership next charges. `locationId` is forwarded only when set.
 * `from`/`to` are never sent (server default window).
 */
export async function listUpcomingMembershipCharges(
  { locationId, page = 0, size = 20 }: ListUpcomingMembershipChargesParams = {},
): Promise<UpcomingMembershipChargesPage> {
  const query: Record<string, string | number | undefined> = { page, size };
  if (locationId !== undefined) query['locationId'] = locationId;

  const body = await request<unknown>('GET', PATH, { query });
  const o = isObject(body) ? body : {};
  const content = extractRows(body)
    .map(parseRow)
    .filter((row): row is UpcomingMembershipCharge => row !== null);
  return {
    content,
    page: asNumber(o['page']) ?? page,
    size: asNumber(o['size']) ?? size,
    totalElements: asNumber(o['totalElements']) ?? content.length,
    totalPages: asNumber(o['totalPages']) ?? (content.length === 0 ? 0 : 1),
    summary: parseSummary(o['summary']),
  };
}
